// src/components/adminLists/AdminLists.jsx
//
// /admin/lists (admins): the restaurant lists of the app (backend
// routes/adminLists.ts). Users' lists become public only once approved
// here; an edit of a public list waits here too. Influencers' lists are
// created here, public at once (JSON in the format the app imports).
// The comments under the lists are public at once: the reported ones (hidden
// after 3 reports) are reviewed here. Photos under the places show only once
// approved here; reported ones (hidden after 3 reports) too. The « Défi du
// mois » is opened in the Défis tab (AdminChallenges.jsx). The users' pseudos
// show only once approved here (« Membre YummiTracker » until then), and so do
// their profiles: photo, social links, bio (Profils à valider).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Container, Typography, Box, Button, Paper, Stack, Chip, Alert, CircularProgress, Tabs, Tab,
  TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Link, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControlLabel, Checkbox,
} from '@mui/material';
import useRoleAuth from '../../utils/useRoleAuth';
import {
  reviewQueue, reportedLists, allLists, approveList, rejectList, hideList, restoreList, deleteList,
  listInfluencers, createInfluencer, updateInfluencer, addInfluencerList, listErrorText,
  reportedComments, hideComment, restoreComment,
  photoQueue, reportedPhotos, approvePhoto, rejectPhoto, hidePhoto, restorePhoto, adminPhotoUrl,
  pseudoQueue, approvePseudo, rejectPseudo,
  profileQueue, approveProfile, rejectProfile, adminProfilePhotoUrl,
} from '../../utils/adminListsApi';
import PendingBadge from '../admin/PendingBadge';
import { pendingCounts } from '../../utils/adminPendingApi';
import AdminChallenges from './AdminChallenges';

const STATUS = {
  draft: { label: 'Brouillon', color: 'default' },
  pending: { label: 'À valider', color: 'warning' },
  public: { label: 'Publique', color: 'success' },
  rejected: { label: 'Refusée', color: 'error' },
  hidden: { label: 'Masquée', color: 'default' },
};
const StatusChip = ({ status }) => <Chip size="small" color={STATUS[status]?.color} label={STATUS[status]?.label || status} />;
const osm = (lat, lon) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=18/${lat}/${lon}`;

function Places({ items }) {
  if (!items?.length) return <Typography variant="body2" color="text.secondary">Aucun lieu.</Typography>;
  return (
    <Table size="small">
      <TableHead><TableRow><TableCell>#</TableCell><TableCell>Lieu</TableCell><TableCell>Mot de l’auteur</TableCell><TableCell>QRProof</TableCell></TableRow></TableHead>
      <TableBody>
        {items.map((i, idx) => (
          <TableRow key={idx}>
            <TableCell>{idx + 1}</TableCell>
            <TableCell>
              <Link href={osm(i.lat, i.lon)} target="_blank" rel="noopener noreferrer">{i.name}</Link>
              {i.address && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{i.address}</Typography>}
            </TableCell>
            <TableCell>{i.note || ''}</TableCell>
            <TableCell>{i.facility ? <Chip size="small" color="primary" label={`★ ${Number(i.facility.note).toFixed(1)}`} /> : ''}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ReviewCard({ list, onApprove, onReject, onHide }) {
  const edit = list.pendingChanges;
  const reject = () => {
    const reason = window.prompt(edit ? 'Motif du refus de la modification (montré à l’auteur)' : 'Motif du refus (montré à l’auteur)');
    if (reason && reason.trim()) onReject(reason.trim());
  };
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`review-${list.id}`}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{list.name}</Typography>
        <StatusChip status={list.status} />
        {edit && <Chip size="small" color="warning" variant="outlined" label="Modification proposée" />}
        {list.reports?.length > 0 && <Chip size="small" color="error" variant="outlined" label={`${list.reports.length} signalement(s)`} />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Par <b>{list.author.name}</b> ({list.author.kind === 'influencer' ? 'influenceur' : 'utilisateur'})
        {list.city ? ` · ${list.city}` : ''} · {list.itemCount} lieu(x) · ❤ {list.likes} · ↓ {list.imports}
      </Typography>
      {list.description && <Typography variant="body2" sx={{ mb: 1 }}>{list.description}</Typography>}
      {list.reports?.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {list.reports.map((r, i) => <div key={i}>{r.reason || 'Signalée sans motif'}</div>)}
        </Alert>
      )}
      {edit ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr' }, gap: 2 }}>
          <Box><Typography variant="subtitle2">Version publiée</Typography><Places items={list.items} /></Box>
          <Box>
            <Typography variant="subtitle2">Proposée : « {edit.name} »{edit.city ? ` · ${edit.city}` : ''}</Typography>
            {edit.description && <Typography variant="body2" sx={{ mb: 1 }}>{edit.description}</Typography>}
            <Places items={edit.items} />
          </Box>
        </Box>
      ) : <Places items={list.items} />}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button variant="contained" color="success" onClick={onApprove}>{edit ? 'Valider la modification' : 'Publier'}</Button>
        <Button variant="outlined" color="error" onClick={reject}>Refuser</Button>
        <Button onClick={() => window.confirm('Masquer cette liste ?') && onHide()}>Masquer</Button>
      </Stack>
    </Paper>
  );
}

function AllLists({ getToken }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [lists, setLists] = useState(null);
  const [error, setError] = useState('');
  const refresh = useCallback(() => allLists(getToken(), { status, q }).then((r) => setLists(r.lists)).catch((e) => setError(listErrorText(e))), [getToken, status, q]);
  useEffect(() => { refresh(); }, [refresh]);
  const act = async (fn) => { setError(''); try { await fn(); refresh(); } catch (e) { setError(listErrorText(e)); } };
  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField select size="small" label="Statut" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">Tous</MenuItem>
          {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Rechercher (liste ou auteur)" value={q} onChange={(e) => setQ(e.target.value)} />
      </Stack>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {!lists ? <CircularProgress /> : (
        <Table size="small">
          <TableHead><TableRow><TableCell>Liste</TableCell><TableCell>Auteur</TableCell><TableCell>Statut</TableCell><TableCell>Lieux</TableCell><TableCell>❤ / ↓</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {lists.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.name}{l.hasPendingChanges && <Chip size="small" variant="outlined" color="warning" label="modif." sx={{ ml: 1 }} />}</TableCell>
                <TableCell>{l.author.name}{l.author.kind === 'influencer' ? ' ✓' : ''}</TableCell>
                <TableCell><StatusChip status={l.status} /></TableCell>
                <TableCell>{l.itemCount}</TableCell>
                <TableCell>{l.likes} / {l.imports}</TableCell>
                <TableCell align="right">
                  {l.status === 'hidden'
                    ? <Button size="small" onClick={() => act(() => restoreList(l.id, getToken()))}>Rétablir</Button>
                    : <Button size="small" onClick={() => act(() => hideList(l.id, getToken()))}>Masquer</Button>}
                  <Button size="small" color="error"
                    onClick={() => window.confirm(`Supprimer « ${l.name} » définitivement ?`) && act(() => deleteList(l.id, getToken()))}>
                    Supprimer
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

const EMPTY_INFLUENCER = { name: '', socialUrl: '', avatarUrl: '', bio: '', verified: true };

function Influencers({ getToken }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // { id?, ...fields }
  const [msg, setMsg] = useState({});
  const refresh = useCallback(() => listInfluencers(getToken()).then((r) => setRows(r.influencers)).catch((e) => setMsg({ error: listErrorText(e) })), [getToken]);
  useEffect(() => { refresh(); }, [refresh]);

  const save = async () => {
    setMsg({});
    const { id, ...data } = editing;
    try {
      if (id) await updateInfluencer(id, data, getToken()); else await createInfluencer(data, getToken());
      setEditing(null); refresh();
    } catch (e) { setMsg({ error: listErrorText(e) }); }
  };

  // One list ({ listName, facilities }) or several ([...]) - the JSON the app imports.
  const importJson = async (influencer, file) => {
    setMsg({});
    try {
      const parsed = JSON.parse(await file.text());
      const all = Array.isArray(parsed) ? parsed : [parsed];
      for (const l of all) await addInfluencerList(influencer.id, l, getToken());
      setMsg({ info: `${all.length} liste(s) publiée(s) pour ${influencer.name}.` });
      refresh();
    } catch (e) {
      setMsg({ error: e instanceof SyntaxError ? 'Fichier JSON illisible.' : listErrorText(e) });
    }
  };

  if (!rows) return <CircularProgress />;
  return (
    <>
      {msg.error && <Alert severity="warning" sx={{ mb: 2 }}>{msg.error}</Alert>}
      {msg.info && <Alert severity="success" sx={{ mb: 2 }}>{msg.info}</Alert>}
      <Button variant="contained" sx={{ mb: 2 }} onClick={() => setEditing({ ...EMPTY_INFLUENCER })}>Ajouter un influenceur</Button>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Les listes d’un influenceur sont publiques dès leur import. Format JSON : celui que l’app importe,
        {' { "listName": "…", "city": "Paris", "facilities": [{ "name": "…", "address": "…", "lat": 48.86, "lon": 2.34 }] } '}
        ou un tableau de plusieurs listes.
      </Typography>
      <Table size="small">
        <TableHead><TableRow><TableCell>Nom</TableCell><TableCell>Réseau</TableCell><TableCell>Listes</TableCell><TableCell /></TableRow></TableHead>
        <TableBody>
          {rows.map((i) => (
            <TableRow key={i.id}>
              <TableCell>{i.name}{i.verified ? ' ✓' : ''}</TableCell>
              <TableCell>{i.socialUrl ? <Link href={i.socialUrl} target="_blank" rel="noopener noreferrer">lien</Link> : ''}</TableCell>
              <TableCell>{i.lists}</TableCell>
              <TableCell align="right">
                <Button size="small" onClick={() => setEditing({ ...EMPTY_INFLUENCER, ...i, socialUrl: i.socialUrl || '', avatarUrl: i.avatarUrl || '', bio: i.bio || '' })}>Modifier</Button>
                <Button size="small" component="label">
                  Importer une liste (JSON)
                  <input hidden type="file" accept="application/json,.json" data-testid={`import-${i.id}`}
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importJson(i, f); }} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? 'Modifier l’influenceur' : 'Nouvel influenceur'}</DialogTitle>
        {editing && (
          <DialogContent>
            <TextField fullWidth margin="dense" label="Nom" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <TextField fullWidth margin="dense" label="Lien réseau social (https://…)" value={editing.socialUrl} onChange={(e) => setEditing({ ...editing, socialUrl: e.target.value })} />
            <TextField fullWidth margin="dense" label="Photo (https://…)" value={editing.avatarUrl} onChange={(e) => setEditing({ ...editing, avatarUrl: e.target.value })} />
            <TextField fullWidth margin="dense" multiline minRows={2} label="Présentation" value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} />
            <FormControlLabel control={<Checkbox checked={editing.verified} onChange={(e) => setEditing({ ...editing, verified: e.target.checked })} />} label="Badge vérifié" />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Annuler</Button>
          <Button variant="contained" onClick={save}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function AdminLists() {
  const { forbidden, loading, getToken } = useRoleAuth(['ROLE_ADMIN']);
  const [tab, setTab] = useState(0);
  // The red badges on the tabs (backend routes/adminPending.ts), counted again after each action.
  const [pending, setPending] = useState(null);
  const refreshPending = useCallback(() => {
    if (loading || forbidden) return;
    pendingCounts(getToken()).then((r) => setPending(r.lists)).catch(() => {});
  }, [loading, forbidden, getToken]);
  useEffect(() => { refreshPending(); }, [refreshPending]);
  const tabLabel = (label, n) => <PendingBadge count={n}><Box component="span" sx={{ pr: n ? 1.5 : 0 }}>{label}</Box></PendingBadge>;
  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (forbidden) return <Container sx={{ py: 4 }}><Alert severity="error">Réservé aux administrateurs.</Alert></Container>;
  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Listes de restaurants</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Les listes des utilisateurs ne sont visibles de tous (catalogue et hit-parade de l’app) qu’une fois publiées ici.
        La modification d’une liste publique attend aussi ici ; la version publiée reste en ligne en attendant.
      </Typography>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2 }}>
        <Tab label={tabLabel('À valider', pending?.review)} /><Tab label={tabLabel('Signalées', pending?.reportedLists)} /><Tab label="Toutes" />
        <Tab label="Influenceurs" /><Tab label={tabLabel('Commentaires signalés', pending?.reportedComments)} />
        <Tab label={tabLabel('Photos à valider', pending?.photosReview)} /><Tab label={tabLabel('Photos signalées', pending?.reportedPhotos)} /><Tab label="Défis" />
        <Tab label={tabLabel('Pseudos à valider', pending?.pseudos)} /><Tab label={tabLabel('Profils à valider', pending?.profiles)} />
      </Tabs>
      {tab === 0 && <ReviewTab load={reviewQueue} getToken={getToken} empty="Rien à valider." onChanged={refreshPending} />}
      {tab === 1 && <ReviewTab load={reportedLists} getToken={getToken} empty="Aucune liste signalée." onChanged={refreshPending} />}
      {tab === 2 && <AllLists getToken={getToken} />}
      {tab === 3 && <Influencers getToken={getToken} />}
      {tab === 4 && <ReportedComments getToken={getToken} onChanged={refreshPending} />}
      {tab === 5 && <PhotoTab load={photoQueue} getToken={getToken} empty="Aucune photo à valider." onChanged={refreshPending} />}
      {tab === 6 && <PhotoTab load={reportedPhotos} getToken={getToken} empty="Aucune photo signalée." onChanged={refreshPending} />}
      {tab === 7 && <AdminChallenges getToken={getToken} />}
      {tab === 8 && <PseudoTab getToken={getToken} onChanged={refreshPending} />}
      {tab === 9 && <ProfileTab getToken={getToken} onChanged={refreshPending} />}
    </Container>
  );
}

/* The review queues: actions get the current token when they run. */
function ReviewTab({ load, getToken, empty, onChanged }) {
  const [lists, setLists] = useState(null);
  const [msg, setMsg] = useState({});
  const refresh = useCallback(() => load(getToken()).then((r) => setLists(r.lists)).catch((e) => setMsg({ error: listErrorText(e) })), [load, getToken]);
  useEffect(() => { refresh(); }, [refresh]);
  async function run(fn, done) {
    setMsg({});
    try { await fn(); setMsg({ info: done }); refresh(); onChanged?.(); } catch (e) { setMsg({ error: listErrorText(e) }); }
  }
  if (!lists) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  return (
    <>
      {msg.error && <Alert severity="warning" sx={{ mb: 2 }}>{msg.error}</Alert>}
      {msg.info && <Alert severity="success" sx={{ mb: 2 }}>{msg.info}</Alert>}
      {!lists.length && <Typography color="text.secondary">{empty}</Typography>}
      {lists.map((l) => (
        <ReviewCard key={l.id} list={l}
          onApprove={() => run(() => approveList(l.id, getToken()), `« ${l.name} » publiée.`)}
          onReject={(reason) => run(() => rejectList(l.id, reason, getToken()), `« ${l.name} » refusée.`)}
          onHide={() => run(() => hideList(l.id, getToken()), `« ${l.name} » masquée.`)} />
      ))}
    </>
  );
}

/* Reported comments: hide one (if 3 reports did not already), or put it back - its reports cleared. */
function ReportedComments({ getToken, onChanged }) {
  const [comments, setComments] = useState(null);
  const [msg, setMsg] = useState({});
  const refresh = useCallback(
    () => reportedComments(getToken()).then((r) => setComments(r.comments)).catch((e) => setMsg({ error: listErrorText(e) })),
    [getToken]);
  useEffect(() => { refresh(); }, [refresh]);
  async function run(fn, done) {
    setMsg({});
    try { await fn(); setMsg({ info: done }); refresh(); onChanged?.(); } catch (e) { setMsg({ error: listErrorText(e) }); }
  }
  if (!comments) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  return (
    <>
      {msg.error && <Alert severity="warning" sx={{ mb: 2 }}>{msg.error}</Alert>}
      {msg.info && <Alert severity="success" sx={{ mb: 2 }}>{msg.info}</Alert>}
      {!comments.length && <Typography color="text.secondary">Aucun commentaire signalé.</Typography>}
      {comments.map((c) => (
        <Paper key={c.id} variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`comment-${c.id}`}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>{c.author.name}</Typography>
            <Typography variant="body2" color="text.secondary">sur « {c.listName} » · {new Date(c.createdAt).toLocaleDateString('fr-FR')}</Typography>
            <Chip size="small" color={c.status === 'hidden' ? 'default' : 'success'} label={c.status === 'hidden' ? 'Masqué' : 'Visible'} />
            {c.reports > 0 && <Chip size="small" color="error" variant="outlined" label={`${c.reports} signalement(s)`} />}
          </Stack>
          <Typography sx={{ whiteSpace: 'pre-line', mb: 1 }}>{c.body}</Typography>
          {c.reasons.length > 0 && <Alert severity="error" sx={{ mb: 1 }}>{c.reasons.map((r, i) => <div key={i}>{r}</div>)}</Alert>}
          <Stack direction="row" spacing={1}>
            {c.status === 'hidden'
              ? <Button variant="outlined" color="success" onClick={() => run(() => restoreComment(c.id, getToken()), 'Commentaire remis en ligne.')}>Remettre en ligne</Button>
              : <Button variant="outlined" color="error" onClick={() => run(() => hideComment(c.id, getToken()), 'Commentaire masqué.')}>Masquer</Button>}
          </Stack>
        </Paper>
      ))}
    </>
  );
}

/* The pseudos waiting for review: approve (shown everywhere at once) or refuse (the app asks for another). */
function PseudoTab({ getToken, onChanged }) {
  const [pseudos, setPseudos] = useState(null);
  const [msg, setMsg] = useState({});
  const refresh = useCallback(
    () => pseudoQueue(getToken()).then(setPseudos).catch((e) => setMsg({ error: listErrorText(e) })),
    [getToken]);
  useEffect(() => { refresh(); }, [refresh]);
  async function run(fn, done) {
    setMsg({});
    try { await fn(); setMsg({ info: done }); refresh(); onChanged?.(); } catch (e) { setMsg({ error: listErrorText(e) }); }
  }
  if (!pseudos) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  return (
    <>
      {msg.error && <Alert severity="warning" sx={{ mb: 2 }}>{msg.error}</Alert>}
      {msg.info && <Alert severity="success" sx={{ mb: 2 }}>{msg.info}</Alert>}
      {!pseudos.length && <Typography color="text.secondary">Aucun pseudo à valider.</Typography>}
      {pseudos.map((u) => (
        <Paper key={u.id} variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`pseudo-${u.id}`}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{u.pseudo}</Typography>
            <Typography variant="body2" color="text.secondary">
              {u.current ? `remplace « ${u.current} »` : 'nouveau'} · demandé le {new Date(u.askedAt ?? u.createdAt).toLocaleDateString('fr-FR')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {u.lists} liste(s) · {u.comments} commentaire(s) · {u.photos} photo(s), affichés sous {u.current ? `« ${u.current} »` : '« Membre YummiTracker »'} en attendant
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" color="success" size="small" onClick={() => run(() => approvePseudo(u.id, getToken()), `« ${u.pseudo} » validé.`)}>Valider</Button>
            <Button variant="outlined" color="error" size="small" onClick={() => run(() => rejectPseudo(u.id, getToken()), `« ${u.pseudo} » refusé.`)}>Refuser</Button>
          </Stack>
        </Paper>
      ))}
    </>
  );
}

const NETWORK_LABELS = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', x: 'X', website: 'Site web' };

/* The links of a profile, each opening in a new tab (to check the account is the author's). */
function SocialLinks({ socials }) {
  const entries = Object.entries(socials || {});
  if (!entries.length) return <Typography variant="body2" color="text.secondary">Aucun lien</Typography>;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {entries.map(([n, url]) => (
        <Chip key={n} size="small" variant="outlined" component="a" href={url} target="_blank" rel="noopener noreferrer"
          clickable label={`${NETWORK_LABELS[n] ?? n} : ${url.replace(/^https:\/\/(www\.)?/, '')}`} />
      ))}
    </Stack>
  );
}

/* The profiles waiting for review (photo, links, bio): approve, or refuse with a reason the author sees. */
function ProfileTab({ getToken, onChanged }) {
  const [profiles, setProfiles] = useState(null);
  const [msg, setMsg] = useState({});
  const refresh = useCallback(
    () => profileQueue(getToken()).then(setProfiles).catch((e) => setMsg({ error: listErrorText(e) })),
    [getToken]);
  useEffect(() => { refresh(); }, [refresh]);
  async function run(fn, done) {
    setMsg({});
    try { await fn(); setMsg({ info: done }); refresh(); onChanged?.(); } catch (e) { setMsg({ error: listErrorText(e) }); }
  }
  const reject = (u) => {
    const reason = window.prompt('Motif du refus (montré à l’auteur)');
    if (reason && reason.trim()) run(() => rejectProfile(u.id, reason.trim(), getToken()), `Profil de ${u.name} refusé.`);
  };
  if (!profiles) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  return (
    <>
      {msg.error && <Alert severity="warning" sx={{ mb: 2 }}>{msg.error}</Alert>}
      {msg.info && <Alert severity="success" sx={{ mb: 2 }}>{msg.info}</Alert>}
      {!profiles.length && <Typography color="text.secondary">Aucun profil à valider.</Typography>}
      {profiles.map((u) => (
        <Paper key={u.id} variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`profile-${u.id}`}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{u.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {u.publicLists} liste(s) publique(s) · demandé le {new Date(u.askedAt).toLocaleDateString('fr-FR')}
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Box>
              <Typography variant="caption" color="text.secondary">Photo proposée</Typography>
              {u.asked.hasPhoto
                ? <AdminPhoto id={u.id} getToken={getToken} load={adminProfilePhotoUrl} round alt={`Photo proposée par ${u.name}`} />
                : <Typography variant="body2" color="text.secondary">Inchangée</Typography>}
            </Box>
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">Liens proposés</Typography>
              <SocialLinks socials={u.asked.socials} />
              <Typography variant="caption" color="text.secondary">Bio proposée</Typography>
              <Typography sx={{ whiteSpace: 'pre-line' }}>{u.asked.bio || '—'}</Typography>
              {(u.current.bio || Object.keys(u.current.socials).length > 0 || u.current.avatarUrl) && (
                <Typography variant="body2" color="text.secondary">
                  Actuellement : {u.current.avatarUrl ? 'une photo · ' : ''}{Object.keys(u.current.socials).length} lien(s){u.current.bio ? ` · « ${u.current.bio} »` : ''}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="contained" color="success" size="small" onClick={() => run(() => approveProfile(u.id, getToken()), `Profil de ${u.name} validé.`)}>Valider</Button>
            <Button variant="outlined" color="error" size="small" onClick={() => reject(u)}>Refuser</Button>
          </Stack>
        </Paper>
      ))}
    </>
  );
}

/* One photo, fetched with the admin's token (pending and hidden ones are not public); `round`: a profile photo. */
function AdminPhoto({ id, getToken, alt, load = adminPhotoUrl, round = false }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    let alive = true;
    Promise.resolve()
      .then(() => load(id, getToken()))
      .then((u) => { url = u; if (alive) setSrc(u); })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL?.(url); };
  }, [id, getToken, load]);
  const box = round ? { width: 120, height: 120, borderRadius: '50%' } : { width: 240, height: 180, borderRadius: 1 };
  if (!src) return <Box sx={{ ...box, bgcolor: '#F1F5F9' }} />;
  return <Box component="img" src={src} alt={alt} sx={{ ...box, objectFit: 'cover', display: 'block' }} />;
}

/* The photos waiting for review, or the reported ones: publish, refuse (with a reason the author sees), hide, restore. */
function PhotoTab({ load, getToken, empty, onChanged }) {
  const [photos, setPhotos] = useState(null);
  const [msg, setMsg] = useState({});
  const refresh = useCallback(
    () => load(getToken()).then((r) => setPhotos(r.photos)).catch((e) => setMsg({ error: listErrorText(e) })),
    [load, getToken]);
  useEffect(() => { refresh(); }, [refresh]);
  async function run(fn, done) {
    setMsg({});
    try { await fn(); setMsg({ info: done }); refresh(); onChanged?.(); } catch (e) { setMsg({ error: listErrorText(e) }); }
  }
  const reject = (p) => {
    const reason = window.prompt('Motif du refus (montré à l’auteur)');
    if (reason && reason.trim()) run(() => rejectPhoto(p.id, reason.trim(), getToken()), 'Photo refusée.');
  };
  if (!photos) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  return (
    <>
      {msg.error && <Alert severity="warning" sx={{ mb: 2 }}>{msg.error}</Alert>}
      {msg.info && <Alert severity="success" sx={{ mb: 2 }}>{msg.info}</Alert>}
      {!photos.length && <Typography color="text.secondary">{empty}</Typography>}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2 }}>
        {photos.map((p) => (
          <Paper key={p.id} variant="outlined" sx={{ p: 1.5 }} data-testid={`photo-${p.id}`}>
            <AdminPhoto id={p.id} getToken={getToken} alt={`Photo de ${p.placeName ?? 'un lieu'} par ${p.author.name}`} />
            <Typography sx={{ fontWeight: 700, mt: 1 }}>{p.placeName ?? 'Lieu retiré de la liste'}</Typography>
            <Typography variant="body2" color="text.secondary">
              « {p.listName} » · par {p.author.name} · {new Date(p.createdAt).toLocaleDateString('fr-FR')}
            </Typography>
            {p.reports > 0 && <Chip size="small" color="error" variant="outlined" label={`${p.reports} signalement(s)`} sx={{ mt: 1 }} />}
            {p.reasons.length > 0 && <Alert severity="error" sx={{ mt: 1 }}>{p.reasons.map((r, i) => <div key={i}>{r}</div>)}</Alert>}
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {p.status === 'pending' && <>
                <Button variant="contained" color="success" size="small" onClick={() => run(() => approvePhoto(p.id, getToken()), 'Photo publiée.')}>Publier</Button>
                <Button variant="outlined" color="error" size="small" onClick={() => reject(p)}>Refuser</Button>
              </>}
              {p.status === 'public' && <Button variant="outlined" color="error" size="small" onClick={() => run(() => hidePhoto(p.id, getToken()), 'Photo masquée.')}>Masquer</Button>}
              {p.status === 'hidden' && <Button variant="outlined" color="success" size="small" onClick={() => run(() => restorePhoto(p.id, getToken()), 'Photo remise en ligne.')}>Remettre en ligne</Button>}
            </Stack>
          </Paper>
        ))}
      </Box>
    </>
  );
}
